import { useState, useMemo } from "react";
import { SearchResultGroup, SearchResultItem } from "../types";
import levenshtein from "fast-levenshtein";
import { useDebouncedState } from "~/utils/useDebouncedInput";

// // // //

function filterResult(props: { item: SearchResultItem; query: string }) {
    const query = props.query.toLowerCase();

    // If the query is empty, return true for all items
    if (query === "") {
        return true;
    }

    const { item } = props;
    const { content } = item;
    const label = content.label.toLowerCase();
    const subtitle = content.subtitle ? content.subtitle.toLowerCase() : "";
    const description = content.description
        ? content.description.toLowerCase()
        : "";

    return (
        label.includes(query) ||
        subtitle.includes(query) ||
        description.includes(query)
    );
}

function calculateMatchScore(item: SearchResultItem, query: string): number {
    const queryLower = query.toLowerCase();
    let scores = [];
    if (item.content.label.toLowerCase().includes(queryLower)) {
        scores.push(
            100 - levenshtein.get(item.content.label.toLowerCase(), queryLower),
        );
    }
    if (
        item.content.subtitle &&
        item.content.subtitle.toLowerCase().includes(queryLower)
    ) {
        scores.push(
            100 -
                levenshtein.get(
                    item.content.subtitle.toLowerCase(),
                    queryLower,
                ),
        );
    }
    if (
        item.content.description &&
        item.content.description.toLowerCase().includes(queryLower)
    ) {
        scores.push(
            100 -
                levenshtein.get(
                    item.content.description.toLowerCase(),
                    queryLower,
                ),
        );
    }

    // Return the highest score found, or 0 if no relevant field contains the query
    return scores.length > 0 ? Math.max(...scores) : 0;
}

export function useFilteredSearchResults(props: {
    groups: SearchResultGroup[];
    initialQuery?: string;
}) {
    const [query, setQuery] = useState(props.initialQuery ?? "");
    const [value, onChange] = useDebouncedState<string>({
        debounceTimeout: 500,
        value: query,
        onChange: setQuery,
    });

    const [open, setOpen] = useState(false);

    // Memoize the flattening of all items from groups
    const items: SearchResultItem[] = useMemo(
        () =>
            props.groups.reduce((allItems, group) => {
                return [
                    ...allItems,
                    ...group.items.map((each) => {
                        return {
                            id: `${group.content.label}-${each.href}`,
                            content: each.content,
                            category: group.content.label,
                            href: each.href,
                        };
                    }),
                ];
            }, [] as SearchResultItem[]),
        [props.groups],
    );

    // Memoize the filtering and sorting of items based on query
    const filteredItems = useMemo(() => {
        let filtered = items.filter((item) => filterResult({ item, query }));

        // Sort the items by relevance if the query is not empty
        if (query !== "") {
            filtered = filtered.sort(
                (a, b) =>
                    calculateMatchScore(b, query) -
                    calculateMatchScore(a, query),
            );
        }

        return filtered;
    }, [items, query]);

    // Memoize the grouping of filtered items by category
    const searchGroupings: Record<string, SearchResultItem[]> = useMemo(() => {
        const grouped = filteredItems.reduce(
            (groups, item) => {
                return {
                    ...groups,
                    [item.category]: [...(groups[item.category] || []), item],
                };
            },
            {} as Record<string, SearchResultItem[]>,
        );

        // When a query is present, sort groups by number of matching items
        if (query === "") {
            return grouped;
        }

        return Object.fromEntries(
            Object.entries(grouped).sort(
                ([, aItems], [, bItems]) => bItems.length - aItems.length,
            ),
        );
    }, [filteredItems, query]);

    return {
        query: value,
        setQuery: onChange,
        open,
        setOpen,
        searchGroupings,
        filteredItems,
    };
}
